import React from 'react';
import { Auth0Provider, useAuth0 } from '@auth0/auth0-react';
import { cimaAuth0Config } from './auth0Config';

const CIMA_API_URL = process.env.REACT_APP_CIMA_API_URL as string;
const CLIENT_ID_HEADER = 'X-Client-ID';

// ---------------------------------------------------------------------------
// Token injection — attach Bearer token + X-Client-ID to every CIMA API call
// ---------------------------------------------------------------------------

/**
 * Returns a fetch wrapper that automatically injects the CIMA access token
 * and X-Client-ID header into every request to the CIMA API.
 *
 * Usage inside components:
 *   const fetchCima = useCimaFetch();
 *   const data = await fetchCima('/intake', { method: 'POST', body: JSON.stringify(payload) });
 */
export function useCimaFetch() {
    const { getAccessTokenSilently } = useAuth0();

    return async (path: string, options: RequestInit = {}): Promise<Response> => {
        const token = await getAccessTokenSilently({
            authorizationParams: {
                audience: cimaAuth0Config.audience,
            },
        });

        const clientIdentifier = process.env.REACT_APP_CIMA_CLIENT_ID || '';

        const headers = new Headers(options.headers);
        headers.set('Authorization', `Bearer ${token}`);
        headers.set(CLIENT_ID_HEADER, clientIdentifier);
        if (!headers.has('Content-Type')) {
            headers.set('Content-Type', 'application/json');
        }

        return fetch(`${CIMA_API_URL}${path}`, { ...options, headers });
    };
}

// ---------------------------------------------------------------------------
// Provider
// ---------------------------------------------------------------------------

interface CimaAuthProviderProps {
    children: React.ReactNode;
}

export function CimaAuthProvider({ children }: CimaAuthProviderProps) {
    return (
        <Auth0Provider
            domain={cimaAuth0Config.domain}
            clientId={cimaAuth0Config.clientId}
            authorizationParams={{
                redirect_uri: cimaAuth0Config.redirectUri,
                audience: cimaAuth0Config.audience,
                scope: 'openid profile email offline_access',
            }}
        >
            {children}
        </Auth0Provider>
    );
}
